import { DatePipe, DecimalPipe } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  inject,
  isDevMode,
  provideAppInitializer,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { NgOtpInputModule } from 'ng-otp-input';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppCommonModule } from './app-common/app-common.module';
import { AppRoutingModule } from './app-routing.module';
import { httpInterceptorProviders } from './authentication/interceptors/httpInterceptorProviders';
import { customTranslateLoader } from './services/custom-translate-loader';
import { appreciateReducer } from './state/appreciate/appreciate.reducer';
import {
  userCollectionReducer,
  usersReducer,
} from './state/user/users.reducer';

import { EffectsModule } from '@ngrx/effects';
import { NgMultiSelectDropDownModule } from 'ng-multiselect-dropdown';
import { ShowRedemptionInfoDialogModule } from './app-common/show-redemption-info-dialog/show-redemption-info-dialog.module';
import { AppComponent } from './app.component';
import { ChatClientPanelModule } from './chat-client-panel/chat-client-panel.module';
import { FeedbackEffects } from './feedback/store/feedback.effects';
import {
  addFeedbackReducer,
  feedbackCategoriesReducer,
  feedbackDetailReducer,
  feedbacksReducer,
} from './feedback/store/feedback.reducer';
import { AuthEffects } from './store/auth/auth.effects';
import { AuthReducer } from './store/auth/auth.reducer';
import { cartReducer } from './store/cart/cart.reducers';
import { DeviceEffects } from './store/device/device.effects';
import { deviceReducer } from './store/device/device.reducer';
import { LayoutEffects } from './store/layout/layout.effects';
import { LayoutReducer } from './store/layout/layout.reducer';
import { RewardEffects } from './store/rewards/rewards.effects';
import {
  rewardDetailReducer,
  rewardsCatAndSubCatPreferenceReducer,
  rewardsReducer,
} from './store/rewards/rewards.reducer';
import { sharedReducer } from './store/shared/shared.reducer';
import { TemporarilyUnavailableModule } from './temporarily-unavailable/temporarily-unavailable.module';

// Commented out old factory function - now using custom loader
// export function TranslationLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http);
// }

const serverErrors = new RegExp(
  `500 Internal Server Error|502 Bad Gateway|503 Service Unavailable|504 Gateway Timeout`,
  'mi',
);

const errors401And404 = /Request failed with status code (401|404)/i;

if (environment.production) {
  Sentry.init({
    dsn: 'https://4b96fe943552483a8b8a2d90c452a540@o1067282.ingest.sentry.io/4505160529018880',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls: [environment.url.sentryReplay],
        networkRequestHeaders: ['X-Custom-Header'],
        networkResponseHeaders: ['X-Custom-Header'],
      }),
    ],
    // Temporarily ignoring "Non-Error exception captured" for now
    beforeSend(event, hint) {
      const originalException = (hint && hint.originalException) as
        | { response?: { status?: number }; status?: number; message?: string }
        | undefined;

      const isNonErrorException =
        event.exception?.values?.[0]?.value?.startsWith(
          'Non-Error exception captured',
        ) ||
        originalException?.message?.startsWith(
          'Non-Error exception captured',
        ) === true;

      if (isNonErrorException) {
        return null;
      }

      const status =
        originalException?.response?.status ?? originalException?.status;

      if (status === 401 || status === 404) {
        return null;
      }
      return event;
    },

    environment: environment.settings.appName,
    tracesSampleRate: 0.01,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: [serverErrors, errors401And404],
  });
}

@NgModule({
  declarations: [AppComponent],
  exports: [],
  bootstrap: [AppComponent],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000',
    }),
    AppCommonModule,
    MatNativeDateModule,
    MatInputModule,
    MatDatepickerModule,
    MatSidenavModule,
    ToastrModule.forRoot(),
    NgOtpInputModule,
    StoreModule.forRoot({
      appreciate: appreciateReducer,
      users: usersReducer,
      userCollection: userCollectionReducer,
      auth: AuthReducer,
      layout: LayoutReducer,
      cart: cartReducer,
      device: deviceReducer,
      shared: sharedReducer,
      rewards: rewardsReducer,
      reward: rewardDetailReducer,
      rewardsCatAndSubCatPreference: rewardsCatAndSubCatPreferenceReducer,
      feedbacks: feedbacksReducer,
      feedbackDetail: feedbackDetailReducer,
      feedbackCategories: feedbackCategoriesReducer,
      addFeedback: addFeedbackReducer,
    }),
    EffectsModule.forRoot([
      AuthEffects,
      LayoutEffects,
      RewardEffects,
      FeedbackEffects,
      DeviceEffects,
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: !isDevMode(), // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      trace: false, //  If set to true, will include stack trace for every dispatched action, so you can see it in trace tab jumping directly to that part of code
      traceLimit: 75, // maximum stack trace frames to be stored (in case trace option was provided as true)
      connectInZone: false, // If set to true, the connection is established inside the Angular zone for better performance
    }),
    NgMultiSelectDropDownModule.forRoot(),
    TemporarilyUnavailableModule,
    ShowRedemptionInfoDialogModule,
    ChatClientPanelModule,
  ],
  providers: [
    DecimalPipe,
    DatePipe,
    httpInterceptorProviders,
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: customTranslateLoader,
        deps: [HttpClient],
      },
    }),
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
